<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import { useI18n } from 'vue-i18n';

import { ppapiClient, emailSchema } from '@lib/pp-shop-lib/helpers';
import { useAlertStore } from '@lib/pp-shop-lib/stores';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const alertStore = useAlertStore();

function queryValue(value) {
    if (Array.isArray(value)) return value[0] || '';
    return typeof value === 'string' ? value : '';
}

const email = computed(() => queryValue(route.query.email).trim());
const tan = computed(() => queryValue(route.query.tan).trim());
const showNewCustomerWelcome = computed(() => route.meta?.showNewCustomerWelcome === true);

const hasValidQuery = computed(() => emailSchema.isValidSync(email.value) && tan.value.length > 0);

const schema = Yup.object().shape({
    password: Yup.string()
        .required(t('auth.validation.passwordRequired'))
        .min(6, t('auth.validation.passwordMin', { min: 6 })),
    passwordRepeat: Yup.string()
        .required(t('auth.resetPassword.validation.passwordRepeatRequired'))
        .oneOf([Yup.ref('password')], t('auth.resetPassword.validation.passwordMismatch'))
});

const apiError = ref('');

async function onSubmit(values) {
    if (!hasValidQuery.value) {
        apiError.value = t('auth.resetPassword.invalidLink');
        return;
    }

    apiError.value = '';

    try {
        await ppapiClient.post('password/reset', {
            email: email.value,
            tan: tan.value,
            password: values.password
        });

        alertStore.success(t('auth.resetPassword.success'));
        router.push({
            path: '/shop/account/login',
            query: { email: email.value }
        });
    } catch (error) {
        apiError.value = String(error || t('auth.resetPassword.error'));
        alertStore.error(apiError.value);
    }
}
</script>

<template>
    <div class="uk-card uk-card-body uk-align-center resetpwd-card">
        <h4  v-if="showNewCustomerWelcome" class="uk-card-title">{{ $t('auth.setPassword.title') }}</h4>
        <h4 v-else class="uk-card-title">{{ $t('auth.resetPassword.title') }}</h4>

        <p v-if="showNewCustomerWelcome" class="uk-text-primary uk-margin-small-bottom">
            {{ $t('auth.resetPassword.welcomeNewCustomer') }}
        </p>

        <p class="uk-text-muted uk-margin-small-bottom">
            {{ $t('auth.resetPassword.info') }}
        </p>

        <div v-if="!hasValidQuery" class="uk-alert-danger" uk-alert>
            {{ $t('auth.resetPassword.invalidLink') }}
        </div>

        <Form class="uk-form-horizontal uk-margin-top" @submit="onSubmit" :validation-schema="schema"
            v-slot="{ errors, isSubmitting }">
            <div class="uk-margin">
                <label class="uk-form-label" for="resetpwd-password">{{ $t('auth.fields.password') }}</label>
                <div class="uk-form-controls">
                    <Field
                        id="resetpwd-password"
                        class="uk-input"
                        name="password"
                        type="password"
                        :disabled="!hasValidQuery"
                        :class="{ 'is-invalid': errors.password }"
                    />
                </div>
                <div class="uk-form-danger">{{ errors.password }}</div>
            </div>

            <div class="uk-margin">
                <label class="uk-form-label" for="resetpwd-password-repeat">{{ $t('auth.resetPassword.passwordRepeat') }}</label>
                <div class="uk-form-controls">
                    <Field
                        id="resetpwd-password-repeat"
                        class="uk-input"
                        name="passwordRepeat"
                        type="password"
                        :disabled="!hasValidQuery"
                        :class="{ 'is-invalid': errors.passwordRepeat }"
                    />
                </div>
                <div class="uk-form-danger">{{ errors.passwordRepeat }}</div>
            </div>

            <div v-if="apiError" class="uk-form-danger uk-margin-small-bottom">{{ apiError }}</div>

            <button class="uk-button uk-button-default uk-button-small" :disabled="isSubmitting || !hasValidQuery">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                {{ $t('auth.resetPassword.submit') }}
            </button>
        </Form>
    </div>
</template>

<style scoped>
.resetpwd-card {
    max-width: 720px;
}
</style>
